import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Camera, 
  Loader2, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Wrench,
  AlertCircle,
  Image as ImageIcon,
  Crown,
  History,
  X,
  Plus,
  Eye,
  RefreshCw,
  BookOpen,
  ChevronRight
} from 'lucide-react';
import LeonAnimated from '../components/LeonAnimated';
import { Button } from '../components/ui/button';
import MobilePhotoUpload from '../components/MobilePhotoUpload';
import { useAdGate, BannerAd } from '../components/AdSystem';
import { LeadCaptureCTA } from '../components/LeadCapture';
import { LeadInterestPopup } from '../components/LeadPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clé localStorage pour la persistance
const STORAGE_KEY = 'leon_diagnostic_draft';

// Messages de chargement progressifs
const LOADING_MESSAGES = [
  { text: "Léon analyse vos photos...", delay: 0 },
  { text: "Détection des anomalies en cours...", delay: 3000 },
  { text: "Identification des matériaux...", delay: 6000 },
  { text: "Analyse des points d'attention...", delay: 9000 },
  { text: "Préparation des recommandations...", delay: 12000 },
  { text: "Finalisation du diagnostic...", delay: 15000 }
];

export default function PhotoDiagnostic() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // État multi-photos (max 3)
  const [photos, setPhotos] = useState([]); // Array of { file: File, preview: string }
  const [description, setDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  
  // Lead popup state
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const isPremium = user?.subscription_plan === 'premium';
  const MAX_PHOTOS = 3;

  // Ad gate hook - show video ad before analysis for free users
  const { requireAd, AdGateModal, isAdShowing } = useAdGate('diagnostic');

  // Charger le brouillon depuis localStorage au montage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.description) {
          setDescription(draft.description);
        }
        // Les photos (base64) sont aussi restaurées
        if (draft.photos && Array.isArray(draft.photos) && draft.photos.length > 0) {
          // Convertir les base64 en objets utilisables
          const restoredPhotos = draft.photos.map((p, idx) => ({
            file: null, // On ne peut pas restaurer le File, mais on garde le preview
            preview: p.preview,
            name: p.name || `Photo ${idx + 1}`,
            restored: true
          }));
          setPhotos(restoredPhotos);
        }
      }
    } catch (e) {
      console.log('Erreur restauration brouillon:', e);
    }
  }, []);

  // Sauvegarder le brouillon à chaque changement
  useEffect(() => {
    if (photos.length > 0 || description) {
      const draft = {
        description,
        photos: photos.map(p => ({
          preview: p.preview,
          name: p.name || p.file?.name || 'Photo'
        })),
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    }
  }, [photos, description]);

  // Effacer le brouillon
  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Ajouter une photo
  const handleAddPhoto = useCallback((file, preview) => {
    if (photos.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos autorisées`);
      return;
    }
    setPhotos(prev => [...prev, { file, preview, name: file?.name }]);
    setError(null);
  }, [photos.length]);

  // Supprimer une photo
  const handleRemovePhoto = useCallback((index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Gérer la sélection de fichier directement (pour les inputs dans la grille)
  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    
    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      setError("Format non supporté. Utilisez JPG, PNG ou HEIC.");
      return;
    }
    
    // Vérifier la taille (10 Mo max)
    if (file.size > 10 * 1024 * 1024) {
      setError("L'image ne doit pas dépasser 10 Mo");
      return;
    }
    
    // Créer la prévisualisation
    const reader = new FileReader();
    reader.onload = (event) => {
      handleAddPhoto(file, event.target.result);
    };
    reader.onerror = () => {
      setError("Impossible de lire l'image");
    };
    reader.readAsDataURL(file);
  }, [handleAddPhoto]);

  // Voir une photo en grand
  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // Lancer l'analyse réelle (appelée après la pub)
  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setError(null);
    setLoadingProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0].text);

    // Messages progressifs
    const messageTimers = LOADING_MESSAGES.slice(1).map((msg, idx) => {
      return setTimeout(() => {
        setLoadingMessage(msg.text);
        setLoadingProgress(Math.min(90, ((idx + 1) / LOADING_MESSAGES.length) * 100));
      }, msg.delay);
    });

    try {
      const formData = new FormData();
      
      // Ajouter toutes les photos
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        if (photo.file) {
          formData.append('images', photo.file);
        } else if (photo.preview && photo.restored) {
          // Pour les photos restaurées (base64), les convertir en Blob
          const response = await fetch(photo.preview);
          const blob = await response.blob();
          const file = new File([blob], photo.name || `photo_${i}.jpg`, { type: 'image/jpeg' });
          formData.append('images', file);
        }
      }
      
      if (description) {
        formData.append('description', description);
      }

      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const response = await fetch(`${API_URL}/api/diagnostic/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });

      // Nettoyer les timers
      messageTimers.forEach(t => clearTimeout(t));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          setError('Limite atteinte. Passez Premium pour continuer.');
          return;
        }
        if (response.status === 401) {
          setError('Session expirée. Veuillez vous reconnecter.');
          return;
        }
        if (response.status === 400) {
          setError(errorData.detail || 'Format d\'image non supporté. Essayez JPG ou PNG.');
          return;
        }
        if (response.status === 500) {
          setError('Une erreur est survenue lors de l\'analyse. Veuillez réessayer.');
          return;
        }
        throw new Error(errorData.detail || 'Erreur lors de l\'analyse');
      }

      const data = await response.json();
      setResult(data);
      setLoadingProgress(100);
      
      // Show lead popup after diagnostic (only for free users)
      if (!isPremium && !leadSubmitted) {
        setTimeout(() => {
          setShowLeadPopup(true);
        }, 2000);
      }
      
      // Effacer le brouillon après succès
      clearDraft();
      
    } catch (err) {
      messageTimers.forEach(t => clearTimeout(t));
      console.log('Error analyzing:', err?.message || err);
      
      // Message d'erreur plus clair selon le type
      let errorMessage = 'Une erreur est survenue lors de l\'analyse. Veuillez réessayer.';
      if (err.message) {
        if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = 'Problème de connexion. Vérifiez votre réseau et réessayez.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'L\'analyse a pris trop de temps. Réessayez avec des photos moins volumineuses.';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Lancer l'analyse avec gate publicitaire
  const analyzeDiagnostic = () => {
    if (photos.length === 0) {
      setError('Veuillez ajouter au moins une photo');
      return;
    }

    // Vérifier que les photos ont des fichiers (non restaurées)
    const validPhotos = photos.filter(p => p.file || p.preview);
    if (validPhotos.length === 0) {
      setError('Veuillez ajouter de nouvelles photos');
      return;
    }

    // Pass through ad gate for free users
    requireAd(() => {
      runAnalysis();
    });
  };

  // Reset complet
  const resetAnalysis = () => {
    setPhotos([]);
    setDescription('');
    setResult(null);
    setError(null);
    clearDraft();
  };

  // Rediriger vers les recettes travaux avec le contexte du diagnostic
  const goToRecipeWithContext = () => {
    if (!result) return;
    
    // Construire le contexte à partir du diagnostic
    let contextParts = [];
    
    // Ajouter l'identification du problème
    if (result.identification) {
      // Nettoyer les emojis et formater
      const cleanId = result.identification
        .replace(/📖|🔍|📊|⚠️/g, '')
        .replace(/\n\n+/g, '\n')
        .trim();
      contextParts.push(cleanId);
    }
    
    // Ajouter les points d'attention principaux
    if (result.attention_points && result.attention_points.length > 0) {
      const mainPoints = result.attention_points
        .slice(0, 2)
        .map(p => p.replace(/🚨/g, '').trim())
        .join('. ');
      if (mainPoints) {
        contextParts.push(`Points d'attention : ${mainPoints}`);
      }
    }
    
    // Ajouter les recommandations principales
    if (result.recommendations && result.recommendations.length > 0) {
      const mainRecs = result.recommendations
        .filter(r => !r.startsWith('💬'))
        .slice(0, 2)
        .join('. ');
      if (mainRecs) {
        contextParts.push(`Recommandations : ${mainRecs}`);
      }
    }
    
    // Créer la description pré-remplie
    const prefilledDescription = contextParts.join('\n\n');
    
    // Stocker le contexte dans sessionStorage pour la page recettes
    const recipeContext = {
      fromDiagnostic: true,
      diagnosticId: result.diagnostic_id,
      description: prefilledDescription,
      timestamp: new Date().toISOString()
    };
    
    sessionStorage.setItem('leon_recipe_context', JSON.stringify(recipeContext));
    
    // Rediriger vers la page recettes
    navigate('/recipes');
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-4">
            <Camera className="w-5 h-5 text-purple-400" />
            <span className="text-purple-400 font-medium">Diagnostic Photo</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-3">
            Analysez vos travaux en photo
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Envoyez jusqu'à 3 photos et Léon vous fournit une analyse détaillée avec des recommandations personnalisées.
          </p>
        </motion.div>

        {/* Zone principale */}
        <AnimatePresence mode="wait">
          {/* ÉTAT: Analyse en cours */}
          {isAnalyzing && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card p-8 sm:p-12"
            >
              <div className="flex flex-col items-center text-center">
                {/* Leon animé */}
                <div className="mb-6">
                  <LeonAnimated size={80} animate={true} />
                </div>
                
                {/* Message de chargement */}
                <motion.p
                  key={loadingMessage}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xl font-heading font-bold text-white mb-4"
                >
                  {loadingMessage}
                </motion.p>
                
                {/* Barre de progression */}
                <div className="w-full max-w-xs mb-6">
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-purple-500 to-gold-500"
                      initial={{ width: '0%' }}
                      animate={{ width: `${loadingProgress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <p className="text-zinc-500 text-sm mt-2">
                    Analyse de {photos.length} photo{photos.length > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Aperçu des photos en cours d'analyse */}
                <div className="flex gap-2 justify-center">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={photo.preview}
                        alt={`Photo ${idx + 1}`}
                        className="w-16 h-16 object-cover rounded-lg border-2 border-purple-500/50"
                      />
                      <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Note rassurante */}
                <p className="text-zinc-500 text-sm mt-6">
                  L'analyse peut prendre jusqu'à 30 secondes selon la complexité.
                </p>
              </div>
            </motion.div>
          )}

          {/* ÉTAT: Résultat */}
          {result && !isAnalyzing && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Photos analysées */}
              <div className="glass-card p-4 mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  {photos.map((photo, idx) => (
                    <img 
                      key={idx}
                      src={photo.preview} 
                      alt={`Photo ${idx + 1}`} 
                      className="w-16 h-16 object-cover rounded-lg border-2 border-emerald-500/50 cursor-pointer hover:border-emerald-400 transition-colors"
                      onClick={() => openViewer(idx)}
                    />
                  ))}
                  <div className="flex-1 ml-2">
                    <p className="text-white font-semibold">{photos.length} photo{photos.length > 1 ? 's' : ''} analysée{photos.length > 1 ? 's' : ''}</p>
                    <p className="text-zinc-500 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Diagnostic terminé
                    </p>
                  </div>
                </div>
              </div>

              {/* Diagnostic */}
              <div className="glass-card p-6 sm:p-8 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <LeonAnimated size={48} animate={false} />
                  <div>
                    <h2 className="text-xl font-heading font-bold text-white">Diagnostic de Léon</h2>
                    <p className="text-zinc-500 text-sm">Analyse complète de vos photos</p>
                  </div>
                </div>

                {/* Identification */}
                {result.identification && (
                  <div className="mb-6">
                    <h3 className="text-gold-500 font-semibold mb-2 flex items-center gap-2">
                      <ImageIcon className="w-5 h-5" />
                      Ce que je vois
                    </h3>
                    <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{result.identification}</p>
                  </div>
                )}

                {/* Points d'attention */}
                {result.attention_points && result.attention_points.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Points d'attention
                    </h3>
                    <ul className="space-y-2">
                      {result.attention_points.map((point, index) => (
                        <li key={index} className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommandations */}
                {result.recommendations && result.recommendations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Mes recommandations
                    </h3>
                    <ul className="space-y-2">
                      {result.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300 text-sm">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions suggérées - filtrer la proposition de recette */}
                {result.suggested_actions && result.suggested_actions.length > 0 && (
                  <div>
                    <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                      <Wrench className="w-5 h-5" />
                      Actions à envisager
                    </h3>
                    <ul className="space-y-2">
                      {result.suggested_actions
                        .filter(action => !action.includes('recette travaux'))
                        .map((action, index) => (
                          <li key={index} className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                            <ArrowRight className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-300 text-sm">{action}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                {/* CTA Recette Travaux - Bouton principal mis en avant */}
                {!result.is_refusal && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-6 p-4 sm:p-5 bg-gradient-to-r from-gold-500/10 to-amber-500/10 border-2 border-gold-500/30 rounded-xl"
                  >
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gold-500/20 rounded-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-gold-500" />
                      </div>
                      <div className="flex-1 text-center sm:text-left">
                        <h4 className="text-white font-semibold text-lg mb-1">
                          Passez à l'action !
                        </h4>
                        <p className="text-zinc-400 text-sm">
                          Léon peut vous générer une recette travaux détaillée pour corriger ce problème.
                        </p>
                      </div>
                      <Button
                        onClick={goToRecipeWithContext}
                        className="w-full sm:w-auto bg-gold-500 hover:bg-gold-400 text-black font-bold py-3 px-6"
                        data-testid="generate-recipe-btn"
                      >
                        <BookOpen className="w-5 h-5 mr-2" />
                        Générer la recette
                        <ChevronRight className="w-5 h-5 ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Lead Capture CTA - Request artisan quotes */}
                <LeadCaptureCTA
                  analysisType="diagnostic"
                  analysisId={result?.diagnostic_id}
                  projectSummary={result?.identification?.slice(0, 200) || description}
                />
              </div>

              {/* Banner Ad for free users */}
              <BannerAd placement="diagnostic_result" className="mt-6" />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <Button
                  onClick={resetAnalysis}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white"
                  data-testid="new-diagnostic-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Nouveau diagnostic
                </Button>
                <Button
                  onClick={() => navigate('/historique')}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  <History className="w-5 h-5 mr-2" />
                  Voir mon historique
                </Button>
              </div>
            </motion.div>
          )}

          {/* ÉTAT: Upload (par défaut) */}
          {!result && !isAnalyzing && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Brouillon restauré */}
              {photos.some(p => p.restored) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-3"
                >
                  <RefreshCw className="w-5 h-5 text-blue-400" />
                  <p className="text-blue-400 text-sm flex-1">
                    Brouillon restauré - Vos photos et texte ont été récupérés
                  </p>
                  <button
                    onClick={resetAnalysis}
                    className="text-zinc-400 hover:text-white text-sm underline"
                  >
                    Effacer
                  </button>
                </motion.div>
              )}

              {/* Zone de photos */}
              <div className="glass-card p-4 sm:p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-heading font-bold text-white">
                      Vos photos
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      Ajoutez jusqu'à {MAX_PHOTOS} photos
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400 text-sm bg-zinc-800/50 px-3 py-1.5 rounded-full" data-testid="photo-counter">
                    <Camera className="w-4 h-4" />
                    <span className="font-medium">{photos.length}/{MAX_PHOTOS}</span>
                  </div>
                </div>

                {/* Grille de photos - 3 colonnes fixes */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="photo-grid">
                  {/* Photos existantes */}
                  {photos.map((photo, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-square rounded-xl overflow-hidden border-2 border-zinc-700 bg-zinc-900"
                    >
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onClick={() => openViewer(index)}
                      />
                      {/* Badge numéro */}
                      <span className="absolute top-1 left-1 w-5 h-5 sm:w-6 sm:h-6 bg-black/80 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </span>
                      {/* Bouton supprimer - toujours visible sur mobile */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemovePhoto(index); }}
                        className="absolute top-1 right-1 w-6 h-6 sm:w-7 sm:h-7 bg-red-500/90 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
                        title="Supprimer"
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                      </button>
                      {/* Badge restauré */}
                      {photo.restored && (
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-blue-500/90 rounded text-[10px] text-white">
                          Restaurée
                        </span>
                      )}
                    </div>
                  ))}
                  
                  {/* Emplacements vides */}
                  {Array.from({ length: MAX_PHOTOS - photos.length }).map((_, emptyIndex) => {
                    const photoNumber = photos.length + emptyIndex + 1;
                    const isFirst = emptyIndex === 0;
                    
                    return (
                      <div 
                        key={`empty-${emptyIndex}`}
                        className={`aspect-square rounded-xl border-2 border-dashed ${
                          isFirst ? 'border-gold-500/50 bg-gold-500/5' : 'border-zinc-700 bg-zinc-900/30'
                        } flex flex-col items-center justify-center p-1`}
                        data-testid={`photo-slot-${photoNumber}`}
                      >
                        {isFirst ? (
                          <>
                            {/* Numéro de la photo */}
                            <span className="text-[10px] text-gold-400 font-medium mb-1">
                              Photo {photoNumber}
                            </span>
                            {/* Deux boutons côte à côte */}
                            <div className="flex gap-1.5">
                              {/* Bouton Caméra */}
                              <button
                                onClick={() => document.getElementById('camera-input-grid').click()}
                                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gold-500 hover:bg-gold-400 flex flex-col items-center justify-center transition-colors shadow-lg"
                                title="Prendre une photo"
                                data-testid="photo-slot-camera-btn"
                              >
                                <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                              </button>
                              {/* Bouton Galerie */}
                              <button
                                onClick={() => document.getElementById('gallery-input-grid').click()}
                                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-zinc-700 hover:bg-zinc-600 flex flex-col items-center justify-center transition-colors"
                                title="Choisir depuis la galerie"
                                data-testid="photo-slot-gallery-btn"
                              >
                                <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Emplacements suivants */}
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-dashed border-zinc-600 flex items-center justify-center">
                              <span className="text-zinc-600 text-sm font-bold">{photoNumber}</span>
                            </div>
                            <span className="text-[10px] sm:text-xs text-zinc-600 text-center mt-1">
                              Optionnel
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Inputs cachés - un seul jeu pour toute la page */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="camera-input-grid"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="gallery-input-grid"
                />

                {/* Boutons d'ajout en dessous de la grille */}
                {photos.length < MAX_PHOTOS && (
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => document.getElementById('camera-input-grid').click()}
                      className="flex-1 py-3 bg-gold-500 hover:bg-gold-400 rounded-xl text-black font-semibold flex items-center justify-center gap-2 transition-colors"
                      data-testid="add-photo-camera-btn"
                    >
                      <Camera className="w-5 h-5" />
                      <span className="text-sm">Prendre une photo</span>
                    </button>
                    <button
                      onClick={() => document.getElementById('gallery-input-grid').click()}
                      className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-white font-medium flex items-center justify-center gap-2 border border-zinc-700 transition-colors"
                      data-testid="add-photo-gallery-btn"
                    >
                      <ImageIcon className="w-5 h-5" />
                      <span className="text-sm">Galerie</span>
                    </button>
                  </div>
                )}

                {/* Conseil multi-angles */}
                {photos.length > 0 && photos.length < MAX_PHOTOS && (
                  <div className="mt-3 p-2.5 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-400 text-xs sm:text-sm flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Ajoutez plusieurs angles pour un diagnostic plus précis.
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Description optionnelle */}
              <div className="glass-card-dark p-4 mb-6">
                <label className="block text-zinc-400 text-sm font-medium mb-2">
                  Décrivez le contexte (optionnel)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Type de pièce, problème observé, ce que vous souhaitez faire..."
                  className="w-full bg-transparent border border-zinc-700 rounded-lg p-3 text-white placeholder-zinc-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none"
                  rows={3}
                  data-testid="diagnostic-description"
                />
              </div>

              {/* Erreur */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-red-400 font-medium">{error}</p>
                        {error.includes('réessayer') && (
                          <p className="text-zinc-500 text-sm mt-1">
                            Vérifiez votre connexion et réessayez.
                          </p>
                        )}
                      </div>
                      {error.includes('Premium') && (
                        <Button
                          onClick={() => navigate('/subscription')}
                          size="sm"
                          className="bg-gold-500 hover:bg-gold-400 text-black"
                        >
                          <Crown className="w-4 h-4 mr-1" />
                          Premium
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bouton d'analyse */}
              <Button
                onClick={analyzeDiagnostic}
                disabled={photos.length === 0}
                className="w-full py-6 text-lg bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="launch-diagnostic-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Lancer le diagnostic ({photos.length} photo{photos.length !== 1 ? 's' : ''})
              </Button>

              {/* Info Premium */}
              {!isPremium && (
                <div className="mt-6 p-4 bg-gold-500/5 border border-gold-500/20 rounded-lg flex items-center gap-4">
                  <Crown className="w-8 h-8 text-gold-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-gold-400 font-semibold text-sm">Version gratuite</p>
                    <p className="text-zinc-500 text-xs">1 diagnostic photo inclus. Passez Premium pour des analyses illimitées.</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal visionneuse photo */}
        <AnimatePresence>
          {viewerOpen && photos[viewerIndex] && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={() => setViewerOpen(false)}
            >
              <button
                onClick={() => setViewerOpen(false)}
                className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              <img
                src={photos[viewerIndex].preview}
                alt={`Photo ${viewerIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              {/* Navigation */}
              {photos.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                  {photos.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setViewerIndex(idx); }}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        idx === viewerIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* Video Ad Modal (before analysis for free users) */}
      <AdGateModal />

      {/* Lead Interest Popup (after diagnostic for free users) */}
      <LeadInterestPopup
        isOpen={showLeadPopup}
        onClose={() => setShowLeadPopup(false)}
        analysisType="diagnostic"
        analysisId={result?.diagnostic_id}
        projectSummary={description || result?.diagnostic?.title || "Diagnostic photo"}
        analysisContent={result?.diagnostic?.description}
        detectedCategory={result?.diagnostic?.work_category || null}
        onLeadSubmitted={() => {
          setLeadSubmitted(true);
          setShowLeadPopup(false);
        }}
      />

      {/* Sticky Banner Ad (bottom, for free users) */}
      {!isPremium && !isAnalyzing && <BannerAd placement="bottom" sticky={true} />}
    </div>
  );
}
